import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link as MuiLink,
  Slider,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Home } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';
import Cropper, { Area } from 'react-easy-crop';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  ResellerCampaignChat,
  ResellerCampaignChatDetails,
  ResellerCampaignDetails,
  getResellerCampaignChatDetails,
  getResellerCampaignChats,
  getResellerCampaignDetails,
  markResellerCampaignChatAsRead,
} from '../../services/campaign-service';
import { saveResellerLogo } from '../../services/admin-service';

const RESELLER_LOGO_WIDTH = 169;
const RESELLER_LOGO_HEIGHT = 62;
const RESELLER_LOGO_CROP_PREVIEW_WIDTH = 180;
const RESELLER_LOGO_CROP_PREVIEW_HEIGHT = 63;
const MIN_LOGO_ZOOM_CONTROL = -0.9;
const MAX_LOGO_ZOOM_CONTROL = 3;

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const CampaignDetailsPage: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [campaign, setCampaign] = useState<ResellerCampaignDetails | null>(null);
  const [chats, setChats] = useState<ResellerCampaignChat[]>([]);
  const [totalChats, setTotalChats] = useState(0);
  const [chatPage, setChatPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatDetailsLoading, setChatDetailsLoading] = useState(false);
  const [chatDetails, setChatDetails] = useState<ResellerCampaignChatDetails | null>(null);
  const [chatModalTab, setChatModalTab] = useState(0);
  const [updatingFollowUp, setUpdatingFollowUp] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [logoSource, setLogoSource] = useState<string | null>(null);
  const [logoCrop, setLogoCrop] = useState({ x: 0, y: 0 });
  const [logoZoomControl, setLogoZoomControl] = useState(0);
  const [logoCroppedAreaPixels, setLogoCroppedAreaPixels] = useState<Area | null>(null);
  const [savingResellerLogo, setSavingResellerLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const logoZoom = Math.max(0.1, 1 + logoZoomControl);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Campanhas', path: '/campaigns' },
      {
        label: campaign?.title || 'Detalhes da Campanha',
      },
    ]);
  }, [setBreadcrumbs, campaign?.title]);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!campaign_id) return;

      setLoadingDetails(true);
      try {
        const response = await getResellerCampaignDetails(campaign_id);
        if (response) {
          setCampaign(response);
        } else {
          setCampaign(null);
          enqueueSnackbar('Falha ao carregar detalhes da campanha', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching campaign details:', error);
        setCampaign(null);
        enqueueSnackbar('Falha ao carregar detalhes da campanha', { variant: 'error' });
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchCampaignDetails();
  }, [campaign_id, enqueueSnackbar]);

  useEffect(() => {
    const fetchCampaignChats = async () => {
      if (!campaign?.id) return;

      setLoadingChats(true);
      try {
        const response = await getResellerCampaignChats(
          campaign.id,
          chatPage * rowsPerPage,
          rowsPerPage
        );
        if (response && Array.isArray(response.items)) {
          setChats(response.items);
          setTotalChats(response.total);
        } else {
          setChats([]);
          setTotalChats(0);
          enqueueSnackbar('Falha ao carregar chats da campanha', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching campaign chats:', error);
        setChats([]);
        setTotalChats(0);
        enqueueSnackbar('Falha ao carregar chats da campanha', { variant: 'error' });
      } finally {
        setLoadingChats(false);
      }
    };

    fetchCampaignChats();
  }, [campaign?.id, chatPage, rowsPerPage, enqueueSnackbar]);

  const isUserMessage = (role: string) => {
    const normalizedRole = role.toLowerCase();
    return normalizedRole.includes('user') || normalizedRole.includes('human');
  };

  const handleOpenChatModal = async (chatId: string) => {
    setChats(prevChats =>
      prevChats.map(chat => (chat.id === chatId ? { ...chat, has_new_messages: false } : chat))
    );
    setChatModalOpen(true);
    setChatDetailsLoading(true);
    setChatDetails(null);
    setChatModalTab(0);

    try {
      const response = await getResellerCampaignChatDetails(chatId);
      if (response) {
        setChatDetails(response);
      } else {
        enqueueSnackbar('Falha ao carregar detalhes do chat', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching chat details:', error);
      enqueueSnackbar('Falha ao carregar detalhes do chat', { variant: 'error' });
    } finally {
      setChatDetailsLoading(false);
    }
  };

  const handleToggleFollowUp = async () => {
    if (!chatDetails?.id || updatingFollowUp) return;

    const nextFollowUpProvided = !(chatDetails.follow_up_provided ?? false);
    setUpdatingFollowUp(true);
    try {
      const response = await markResellerCampaignChatAsRead(chatDetails.id);
      if (response === null) {
        enqueueSnackbar('Falha ao atualizar status de atendimento', { variant: 'error' });
        return;
      }

      setChatDetails(prevDetails =>
        prevDetails ? { ...prevDetails, follow_up_provided: nextFollowUpProvided } : prevDetails
      );
      setChats(prevChats =>
        prevChats.map(chat =>
          chat.id === chatDetails.id ? { ...chat, follow_up_provided: nextFollowUpProvided } : chat
        )
      );
      enqueueSnackbar('Status de atendimento atualizado com sucesso', { variant: 'success' });
    } catch (error) {
      console.error('Error updating follow up status:', error);
      enqueueSnackbar('Falha ao atualizar status de atendimento', { variant: 'error' });
    } finally {
      setUpdatingFollowUp(false);
    }
  };

  const handleCloseChatModal = () => {
    setChatModalOpen(false);
    setChatDetails(null);
    setChatModalTab(0);
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });

  const getCroppedResellerLogo = async (
    imageSrc: string,
    croppedAreaPixels: Area
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Nao foi possivel inicializar o canvas para recorte.');
    }

    canvas.width = Math.max(1, Math.round(croppedAreaPixels.width));
    canvas.height = Math.max(1, Math.round(croppedAreaPixels.height));

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = RESELLER_LOGO_WIDTH;
    resizedCanvas.height = RESELLER_LOGO_HEIGHT;

    const resizedCtx = resizedCanvas.getContext('2d');
    if (!resizedCtx) {
      throw new Error('Nao foi possivel redimensionar o logo.');
    }

    resizedCtx.drawImage(canvas, 0, 0, RESELLER_LOGO_WIDTH, RESELLER_LOGO_HEIGHT);

    return new Promise((resolve, reject) => {
      resizedCanvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Falha ao preparar imagem para upload.'));
          return;
        }
        resolve(blob);
      }, 'image/png');
    });
  };

  const resetLogoDialogState = () => {
    setLogoSource(null);
    setLogoCrop({ x: 0, y: 0 });
    setLogoZoomControl(0);
    setLogoCroppedAreaPixels(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleOpenLogoDialog = () => {
    resetLogoDialogState();
    setLogoDialogOpen(true);
  };

  const handleCloseLogoDialog = () => {
    setLogoDialogOpen(false);
    resetLogoDialogState();
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        setLogoSource(result);
        setLogoCrop({ x: 0, y: 0 });
        setLogoZoomControl(0);
      }
    };
    reader.onerror = () => {
      enqueueSnackbar('Nao foi possivel ler a imagem selecionada', { variant: 'error' });
    };
    reader.readAsDataURL(file);
  };

  const onLogoCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setLogoCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveResellerLogo = async () => {
    if (!campaign) return;

    if (!logoSource || !logoCroppedAreaPixels) {
      enqueueSnackbar('Selecione uma imagem para continuar', { variant: 'warning' });
      return;
    }

    const resellerUserId = campaign.reseller_id || campaign.id || campaign_id;
    if (!resellerUserId) {
      enqueueSnackbar('Nao foi possivel identificar a revenda para salvar o logo.', {
        variant: 'error',
      });
      return;
    }

    setSavingResellerLogo(true);
    try {
      const croppedLogo = await getCroppedResellerLogo(logoSource, logoCroppedAreaPixels);
      const response = await saveResellerLogo(resellerUserId, croppedLogo);
      if (response) {
        enqueueSnackbar('Logo da revenda salvo com sucesso', { variant: 'success' });
        handleCloseLogoDialog();
        if ('caches' in window) {
          try {
            const cacheKeys = await window.caches.keys();
            await Promise.all(cacheKeys.map(key => window.caches.delete(key)));
          } catch (cacheError) {
            console.warn('Falha ao limpar cache do navegador:', cacheError);
          }
        }
        window.location.replace(`${window.location.pathname}?_ts=${Date.now()}`);
      } else {
        enqueueSnackbar('Falha ao salvar logo da revenda', { variant: 'error' });
      }
    } catch (error) {
      console.error('Erro ao salvar logo da revenda:', error);
      enqueueSnackbar('Falha ao salvar logo da revenda', { variant: 'error' });
    } finally {
      setSavingResellerLogo(false);
    }
  };

  const handleChangeChatsPage = (_event: unknown, newPage: number) => {
    setChatPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setChatPage(0);
  };

  useEffect(() => {
    setChatPage(0);
  }, [campaign?.id]);

  const renderedTemplateContent = useMemo(() => {
    if (!campaign?.content) {
      return (
        <Typography color="text.secondary">
          Nenhum conteúdo disponível para esta campanha.
        </Typography>
      );
    }

    if (campaign.template_type === 'image') {
      const content = campaign.content;
      const imageSrc =
        content.startsWith('data:') ||
        content.startsWith('http://') ||
        content.startsWith('https://') ||
        content.startsWith('blob:')
          ? content
          : `data:image/png;base64,${content}`;

      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <img src={imageSrc} alt={campaign.title} style={{ maxWidth: '100%', borderRadius: 8 }} />
        </Box>
      );
    }

    if (campaign.template_type === 'html') {
      return <Box dangerouslySetInnerHTML={{ __html: campaign.content }} />;
    }

    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {campaign.content}
      </Typography>
    );
  }, [campaign]);

  if (loadingDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Campanha não encontrada
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaigns')} sx={{ mt: 2 }}>
          Voltar para Campanhas
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaigns')}>
          Voltar para Campanhas
        </Button>
        <Button
          variant="outlined"
          onClick={handleOpenLogoDialog}
          sx={{
            borderColor: 'var(--acer-green, #83b81a)',
            color: 'var(--acer-green, #83b81a)',
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: 'var(--acer-green, #83b81a)',
              backgroundColor: 'rgba(131, 184, 26, 0.08)',
            },
          }}
        >
          Atualizar Logo da Revenda
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações da Campanha
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Título</Typography>
                  <Typography variant="body2">{campaign.title || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Descrição</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {campaign.description || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Público Alvo</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {campaign.target_audience || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Revenda</Typography>
                  <Typography variant="body2">{campaign.reseller_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Email</Typography>
                  <Typography variant="body2">{campaign.reseller_email || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Link da Campanha</Typography>
                  {campaign.url ? (
                    <MuiLink
                      href={campaign.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {campaign.url}
                    </MuiLink>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Chats</Typography>
                  <Typography variant="body2">{campaign.started_chats}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Leads</Typography>
                  <Typography variant="body2">{campaign.leads_count}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Solicitações de orçamento</Typography>
                  <Typography variant="body2">{campaign.requested_quotes}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Criada em</Typography>
                  <Typography variant="body2">{formatDate(campaign.created_at)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Template da Campanha
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                {renderedTemplateContent}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Chats da Campanha
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {loadingChats ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Identificação</TableCell>
                          <TableCell align="center">Lead</TableCell>
                          <TableCell align="center">Orçamento</TableCell>
                          <TableCell align="center">Mensagens</TableCell>
                          <TableCell align="center">Follow Up</TableCell>
                          <TableCell align="right">Criado em</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chats.length > 0 ? (
                          chats.map(chat => (
                            <TableRow
                              key={chat.id}
                              hover
                              onClick={() => handleOpenChatModal(chat.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: chat.has_new_messages
                                        ? 'success.main'
                                        : 'grey.500',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {chat.identification || 'Não identificado'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={chat.is_lead ? 'Sim' : 'Não'}
                                  color={chat.is_lead ? 'success' : 'default'}
                                  variant={chat.is_lead ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={chat.request_quote ? 'Sim' : 'Não'}
                                  color={chat.request_quote ? 'success' : 'default'}
                                  variant={chat.request_quote ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell align="center">{chat.messages ?? 0}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={chat.follow_up_provided ? 'Sim' : 'Não'}
                                  color={chat.follow_up_provided ? 'success' : 'default'}
                                  variant={chat.follow_up_provided ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell align="right">{formatDate(chat.created_at)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              Nenhum chat encontrado para esta campanha
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalChats}
                    page={chatPage}
                    onPageChange={handleChangeChatsPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Linhas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={chatModalOpen}
        onClose={handleCloseChatModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h6">Detalhes do Chat</Typography>
            <Button
              variant="outlined"
              onClick={handleToggleFollowUp}
              disabled={chatDetailsLoading || !chatDetails || updatingFollowUp}
              sx={{
                borderColor:
                  (chatDetails?.follow_up_provided ?? false) ? 'error.main' : 'success.main',
                color: (chatDetails?.follow_up_provided ?? false) ? 'error.main' : 'success.main',
                backgroundColor: '#fff',
                '&:hover': {
                  borderColor:
                    (chatDetails?.follow_up_provided ?? false) ? 'error.dark' : 'success.dark',
                  backgroundColor: '#fff',
                },
              }}
            >
              {updatingFollowUp ? (
                <CircularProgress size={18} color="inherit" />
              ) : chatDetails?.follow_up_provided ? (
                'Marcar Como não Atendido'
              ) : (
                'Marcar Como Atendido'
              )}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {chatDetailsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
              <CircularProgress />
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              gap="16px"
              height="100%"
              sx={{ overflowY: 'auto', pr: 1 }}
            >
              {chatDetails ? (
                <>
                  <Tabs
                    value={chatModalTab}
                    onChange={(_event, value: number) => setChatModalTab(value)}
                    variant="fullWidth"
                  >
                    <Tab label="Informações" />
                    <Tab label="Produtos" />
                    <Tab label="Mensagens" />
                  </Tabs>

                  <Divider />

                  {chatModalTab === 0 ? (
                    <Box>
                      <Typography variant="subtitle2">Identificação</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {chatDetails.identification || 'Não identificado'}
                      </Typography>

                      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={`Lead: ${chatDetails.is_lead ? 'Sim' : 'Não'}`}
                          color={chatDetails.is_lead ? 'success' : 'default'}
                          variant={chatDetails.is_lead ? 'filled' : 'outlined'}
                        />
                        <Chip
                          size="small"
                          label={`Orçamento: ${chatDetails.request_quote ? 'Sim' : 'Não'}`}
                          color={chatDetails.request_quote ? 'success' : 'default'}
                          variant={chatDetails.request_quote ? 'filled' : 'outlined'}
                        />
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">CNPJ</Typography>
                          <Typography variant="body2">{chatDetails.cnpj || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Contato</Typography>
                          <Typography variant="body2">{chatDetails.contact || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Endereço</Typography>
                          <Typography variant="body2">{chatDetails.address || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Orçamento</Typography>
                          <Typography variant="body2">{chatDetails.budget || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Autoridade</Typography>
                          <Typography variant="body2">{chatDetails.authority || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Necessidade</Typography>
                          <Typography variant="body2">{chatDetails.need || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Prazo</Typography>
                          <Typography variant="body2">{chatDetails.timeframe || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2">Atualizado em</Typography>
                          <Typography variant="body2">
                            {chatDetails.updated_at ? formatDate(chatDetails.updated_at) : '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : null}

                  {chatModalTab === 1 ? (
                    <Box>
                      {chatDetails.products?.length ? (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          {chatDetails.products.map(product => (
                            <Box
                              key={product.id}
                              display="flex"
                              alignItems="center"
                              gap={1.5}
                              sx={{
                                border: '1px solid var(--acer-lightgray)',
                                borderRadius: 1.5,
                                p: 1.25,
                              }}
                            >
                              <Box
                                component="img"
                                src={product.image}
                                alt={product.name}
                                sx={{
                                  width: 56,
                                  height: 56,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  backgroundColor: 'var(--acer-lightgray)',
                                }}
                              />
                              <Box flex={1}>
                                <Typography variant="subtitle2">{product.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Quantidade: {product.quantity ?? 0}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
                          Nenhum produto encontrado para este chat
                        </Typography>
                      )}
                    </Box>
                  ) : null}

                  {chatModalTab === 2 ? (
                    <>
                      {chatDetails.messages.length > 0 ? (
                        chatDetails.messages.map(message => {
                          const isUser = isUserMessage(message.role);

                          return (
                            <Box
                              key={message.id}
                              width="100%"
                              display="flex"
                              justifyContent={isUser ? 'flex-end' : 'flex-start'}
                            >
                              <Box
                                maxWidth="80%"
                                width="fit-content"
                                bgcolor={isUser ? 'var(--acer-green)' : 'var(--acer-lightgray)'}
                                padding="12px 16px"
                              >
                                {isUser ? (
                                  <Typography color="white" fontSize="14px" lineHeight="1.625">
                                    {message.message}
                                  </Typography>
                                ) : (
                                  <Box
                                    color="var(--acer-dark)"
                                    fontSize="14px"
                                    lineHeight="1.625"
                                    sx={{
                                      '& p': { margin: 0 },
                                      '& p + p': { marginTop: '8px' },
                                      '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
                                      '& code': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                        padding: '2px 4px',
                                        borderRadius: '4px',
                                        fontFamily: 'monospace',
                                      },
                                      '& pre': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        overflowX: 'auto',
                                      },
                                      '& pre code': {
                                        backgroundColor: 'transparent',
                                        padding: 0,
                                      },
                                    }}
                                  >
                                    <ReactMarkdown>{message.message}</ReactMarkdown>
                                  </Box>
                                )}

                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ mt: 0.75 }}
                                  color={isUser ? 'rgba(255,255,255,0.85)' : 'text.secondary'}
                                >
                                  {formatDate(message.created_at)}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
                          Nenhuma mensagem encontrada para este chat
                        </Typography>
                      )}
                    </>
                  ) : null}
                </>
              ) : (
                <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                  Nenhum detalhe disponível para este chat
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={logoDialogOpen} onClose={handleCloseLogoDialog} fullWidth maxWidth="sm">
        <DialogTitle>Atualizar Logo da Revenda</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Inclua preferencialmente uma imagem no tamanho 169x62 para melhor resultado no
              recorte.
            </Typography>
            <Button variant="outlined" onClick={() => logoInputRef.current?.click()}>
              Escolher Imagem
            </Button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
              style={{ display: 'none' }}
            />

            {logoSource ? (
              <>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 320,
                    bgcolor: '#111111',
                    overflow: 'hidden',
                  }}
                >
                  <Cropper
                    image={logoSource}
                    crop={logoCrop}
                    zoom={logoZoom}
                    minZoom={0.1}
                    aspect={RESELLER_LOGO_WIDTH / RESELLER_LOGO_HEIGHT}
                    cropSize={{
                      width: RESELLER_LOGO_CROP_PREVIEW_WIDTH,
                      height: RESELLER_LOGO_CROP_PREVIEW_HEIGHT,
                    }}
                    onCropChange={setLogoCrop}
                    onZoomChange={value => setLogoZoomControl(value - 1)}
                    onCropComplete={onLogoCropComplete}
                    restrictPosition={false}
                    showGrid
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Zoom
                  </Typography>
                  <Slider
                    min={MIN_LOGO_ZOOM_CONTROL}
                    max={MAX_LOGO_ZOOM_CONTROL}
                    step={0.01}
                    value={logoZoomControl}
                    onChange={(_, value) => setLogoZoomControl(value as number)}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  O logo será salvo em 120px de largura por 42px de altura.
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">
                Selecione uma imagem para posicionar e ajustar o zoom.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveResellerLogo}
            disabled={!logoSource || savingResellerLogo}
          >
            {savingResellerLogo ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignDetailsPage;
